import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type {
  CapitalizeCipProject,
  CipApprovalDecision,
  CipApprovalReject,
  CipBorrowingDisclosure,
  CipBorrowingPreviewRow,
  CipBorrowingRunStatus,
  CipCapRateResult,
  CipCapitalizeRequestStatus,
  CipCostEntry,
  CipProject,
  CipProjectListRow,
  CipProjectStatus,
  CipRetentionSummary,
  CipRollforwardRow,
  CreateCipCostEntry,
  CreateCipProject,
  CreateCipRetentionRelease,
  MarkReadyCip,
  ResumeCipProject,
  SuspendCipProject,
  WriteOffCipProject,
} from '@dcc-erp/shared';

import { api, apiJson, withSodOverride } from '../api';

// ============================================================
// Phase 3b approval-gating row shapes (engine-managed CB run + CI request).
// The shared package only exports the status string-unions + decision DTOs;
// these light row types mirror the API's run/request + materialized chain so the
// UI can render the approval timeline the same way /ap does for an AP bill.
// ============================================================

// One persisted approval-chain step (maker→reviewer(s)→approver), as returned by
// the API include `approvals: { orderBy: stepNo }`.
export interface CipApprovalRow {
  stepNo: number;
  roleCode: string;
  roleLabel: string;
  approvedAt: string | null;
  approvedBy: string | null;
  comment: string | null;
}

// A monthly TFRS 23 borrowing-cost run (docType CB). Accounting `status` walks
// DRAFT → POSTED/CANCELLED on terminal transitions; `approvalStatus` walks the
// neutral engine machine (DRAFT|PENDING|APPROVED|REJECTED|RETURNED).
export interface CipBorrowingRunWithChain {
  id: string;
  period: string;
  voucherNo: string;
  capRate: string;
  totalAmount: string;
  status: CipBorrowingRunStatus;
  approvalStatus: string | null;
  currentStep: number;
  submittedBy: string | null;
  submittedAt: string | null;
  approvedBy: string | null;
  approvedAt: string | null;
  rejectedReason: string | null;
  returnedReason: string | null;
  approvals: CipApprovalRow[];
}

// A CIP → FA capitalization request (docType CI) holding the replayable payload.
export interface CipCapitalizeRequestWithChain {
  id: string;
  cipProjectId: string;
  voucherNo: string;
  capitalDate: string;
  amount: string;
  status: CipCapitalizeRequestStatus;
  approvalStatus: string | null;
  currentStep: number;
  submittedBy: string | null;
  submittedAt: string | null;
  approvedBy: string | null;
  approvedAt: string | null;
  rejectedReason: string | null;
  returnedReason: string | null;
  journalEntryId: string | null;
  approvals: CipApprovalRow[];
}

// Cache-key scopes — keyed by companyId so DCC/TTOP/RCI switches give
// independent caches (matches the AR/projects pattern).
const projectsKey = (companyId: string | undefined) => ['cip-projects', companyId] as const;
const entriesKey = (companyId: string | undefined, cipProjectId: string | undefined) =>
  ['cip-projects', companyId, cipProjectId, 'cost-entries'] as const;
const capRateKey = (companyId: string | undefined, period: string | undefined) =>
  ['cip-projects', companyId, 'borrowing', 'cap-rate', period] as const;
const previewKey = (companyId: string | undefined, period: string | undefined) =>
  ['cip-projects', companyId, 'borrowing', 'preview', period] as const;
const disclosureKey = (companyId: string | undefined, year: string | undefined) =>
  ['cip-projects', companyId, 'borrowing', 'disclosure', year] as const;
const runsKey = (companyId: string | undefined, period: string | undefined) =>
  ['cip-projects', companyId, 'borrowing', 'runs', period ?? null] as const;
const capRequestsKey = (companyId: string | undefined, cipProjectId: string | undefined) =>
  ['cip-projects', companyId, cipProjectId, 'capitalize-requests'] as const;
const retentionKey = (companyId: string | undefined, cipProjectId: string | undefined) =>
  ['cip-projects', companyId, cipProjectId, 'retention'] as const;
// Phase 4b — the AP-bill → CIP clearing inbox, keyed by the optional project
// filter so a project-scoped view and the all-projects view cache independently.
const clearingInboxKey = (companyId: string | undefined, cipProjectId: string | undefined) =>
  ['cip-projects', companyId, 'clearing-inbox', cipProjectId ?? null] as const;

// One recorded retention release (Dr RETENTION_PAYABLE / Cr cash), as returned in
// the `releases` array of GET :id/retention.
export interface CipRetentionRelease {
  id: string;
  vendorName: string | null;
  amount: string;
  releaseDate: string;
  journalEntryId: string | null;
  postedBy: string;
  createdAt: string;
}

// GET :id/retention payload — withheld/released/outstanding summary + release log.
export interface CipRetentionResponse {
  summary: CipRetentionSummary;
  releases: CipRetentionRelease[];
}

export interface CipProjectFilters {
  status?: CipProjectStatus;
  projectId?: string;
}

export function useCipProjects(companyId: string | undefined, filters: CipProjectFilters = {}) {
  const qs = new URLSearchParams();
  if (filters.status) qs.set('status', filters.status);
  if (filters.projectId) qs.set('projectId', filters.projectId);
  const search = qs.toString();
  return useQuery({
    queryKey: [...projectsKey(companyId), filters],
    queryFn: () =>
      api<CipProjectListRow[]>(`/companies/${companyId}/cip-projects${search ? `?${search}` : ''}`),
    enabled: !!companyId,
  });
}

export function useCreateCipProject(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateCipProject) =>
      api<CipProject>(`/companies/${companyId}/cip-projects`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: projectsKey(companyId) });
    },
  });
}

export function useCipCostEntries(companyId: string | undefined, cipProjectId: string | undefined) {
  return useQuery({
    queryKey: entriesKey(companyId, cipProjectId),
    queryFn: () =>
      api<CipCostEntry[]>(`/companies/${companyId}/cip-projects/${cipProjectId}/cost-entries`),
    enabled: !!companyId && !!cipProjectId,
  });
}

export function useAddCipCostEntry(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      cipProjectId,
      data,
      idempotencyKey,
    }: {
      cipProjectId: string;
      data: CreateCipCostEntry;
      idempotencyKey?: string;
    }) =>
      api<CipCostEntry>(`/companies/${companyId}/cip-projects/${cipProjectId}/cost-entries`, {
        method: 'POST',
        headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {},
        body: JSON.stringify(data),
      }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: projectsKey(companyId) });
      void qc.invalidateQueries({ queryKey: entriesKey(companyId, vars.cipProjectId) });
      // The JV is visible in /journal-entries — refresh that list too.
      void qc.invalidateQueries({ queryKey: ['journal-entries', companyId] });
    },
  });
}

// Submit a CIP → FA capitalization for approval (Phase 3b — 4-eyes). The endpoint
// now creates a CipCapitalizeRequest AND submits it → returns the PENDING request
// with its chain. The Dr 1210 / Cr 1240 JV + FA registration run ONLY on final
// approval. The call site (the wizard) is unchanged — only the success UX is:
// "ส่งคำขออนุมัติแล้ว — รออนุมัติ". Invalidates the project's capitalize-requests
// so the wizard flips to the pending-approval view.
export function useCapitalizeCipProject(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      cipProjectId,
      data,
      idempotencyKey,
    }: {
      cipProjectId: string;
      data: CapitalizeCipProject;
      idempotencyKey?: string;
    }) =>
      api<CipCapitalizeRequestWithChain>(
        `/companies/${companyId}/cip-projects/${cipProjectId}/capitalize`,
        {
          method: 'POST',
          headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {},
          body: JSON.stringify(data),
        },
      ),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: projectsKey(companyId) });
      void qc.invalidateQueries({ queryKey: ['journal-entries', companyId] });
      void qc.invalidateQueries({ queryKey: capRequestsKey(companyId, vars.cipProjectId) });
    },
  });
}

export function useMarkReadyCipProject(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ cipProjectId, data }: { cipProjectId: string; data: MarkReadyCip }) =>
      api<CipProject>(`/companies/${companyId}/cip-projects/${cipProjectId}/mark-ready`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: projectsKey(companyId) });
      void qc.invalidateQueries({ queryKey: ['journal-entries', companyId] });
    },
  });
}

export function useWriteOffCipProject(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      cipProjectId,
      data,
      idempotencyKey,
    }: {
      cipProjectId: string;
      data: WriteOffCipProject;
      idempotencyKey?: string;
    }) =>
      api<{ project: CipProject; journalEntryId: string | null }>(
        `/companies/${companyId}/cip-projects/${cipProjectId}/write-off`,
        {
          method: 'POST',
          headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {},
          body: JSON.stringify(data),
        },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: projectsKey(companyId) });
      void qc.invalidateQueries({ queryKey: ['journal-entries', companyId] });
    },
  });
}

export interface CipRollforwardFilters {
  branchId?: string;
  departmentId?: string;
}

export function useCipRollforward(
  companyId: string | undefined,
  period: string | undefined,
  filters: CipRollforwardFilters = {},
) {
  const qs = new URLSearchParams();
  if (period) qs.set('period', period);
  if (filters.branchId) qs.set('branchId', filters.branchId);
  if (filters.departmentId) qs.set('departmentId', filters.departmentId);
  const search = qs.toString();
  return useQuery({
    queryKey: [...projectsKey(companyId), 'rollforward', period, filters],
    queryFn: () =>
      api<CipRollforwardRow[]>(
        `/companies/${companyId}/cip-projects/reports/rollforward${search ? `?${search}` : ''}`,
      ),
    enabled: !!companyId && !!period,
  });
}

// ============================================================
// Phase 5 (IAS 23.20) — suspend / resume capitalization. Suspending an ACTIVE
// project stamps suspendedAt/suspendReason and excludes it from the next TFRS 23
// allocation; resuming returns it to ACTIVE (re-accrues from the next run).
// Both invalidate cip-projects so the register/detail status badge refreshes.
// ============================================================

export function useSuspendCipProject(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ cipProjectId, data }: { cipProjectId: string; data: SuspendCipProject }) =>
      api<CipProject>(`/companies/${companyId}/cip-projects/${cipProjectId}/suspend`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: projectsKey(companyId) });
    },
  });
}

export function useResumeCipProject(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ cipProjectId, data }: { cipProjectId: string; data: ResumeCipProject }) =>
      api<CipProject>(`/companies/${companyId}/cip-projects/${cipProjectId}/resume`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: projectsKey(companyId) });
    },
  });
}

// ============================================================
// TFRS 23 borrowing-cost engine (Phase 3a)
// ============================================================

// Weighted capitalization rate of the company's general-loan pool for a period.
export function useCipCapRate(companyId: string | undefined, period: string | undefined) {
  return useQuery({
    queryKey: capRateKey(companyId, period),
    queryFn: () =>
      api<CipCapRateResult>(
        `/companies/${companyId}/cip-projects/borrowing/cap-rate?period=${period}`,
      ),
    enabled: !!companyId && !!period,
  });
}

// IAS 23.26 disclosure for a year — total borrowing cost capitalized + the
// per-period capitalization rate(s) used, aggregated from the posted CB runs.
// Drives the disclosure card in the borrowing tab (keyed by the header year).
export function useCipBorrowingDisclosure(companyId: string | undefined, year: string | undefined) {
  return useQuery({
    queryKey: disclosureKey(companyId, year),
    queryFn: () =>
      api<CipBorrowingDisclosure>(
        `/companies/${companyId}/cip-projects/borrowing/disclosure?year=${year}`,
      ),
    enabled: !!companyId && !!year,
  });
}

// Preview the per-project TFRS 23 allocation for a period (no posting). The
// endpoint is POST { period } but is read-only — expose it as a query keyed by
// period so the borrowing tab re-fetches when the period changes.
export function useCipBorrowingPreview(companyId: string | undefined, period: string | undefined) {
  return useQuery({
    queryKey: previewKey(companyId, period),
    queryFn: () =>
      api<CipBorrowingPreviewRow[]>(`/companies/${companyId}/cip-projects/borrowing/preview`, {
        method: 'POST',
        body: JSON.stringify({ period }),
      }),
    enabled: !!companyId && !!period,
  });
}

// Submit the monthly borrowing-cost run for approval (Phase 3b — 4-eyes).
// The endpoint now creates a DRAFT run AND submits it to the central engine →
// returns a PENDING run with its materialized chain. The borrowing JVs post
// ONLY on final approval (the call site is unchanged — only the SEMANTICS are:
// "ส่งอนุมัติ", not "post now"). Invalidates the runs list so the borrowing tab
// + run modal reflect the new PENDING run.
export function useRunCipBorrowing(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ period }: { period: string }) =>
      api<CipBorrowingRunWithChain>(`/companies/${companyId}/cip-projects/borrowing/run`, {
        method: 'POST',
        body: JSON.stringify({ period }),
      }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: projectsKey(companyId) });
      void qc.invalidateQueries({ queryKey: ['journal-entries', companyId] });
      void qc.invalidateQueries({ queryKey: capRateKey(companyId, vars.period) });
      void qc.invalidateQueries({ queryKey: previewKey(companyId, vars.period) });
      void qc.invalidateQueries({ queryKey: runsKey(companyId, vars.period) });
      void qc.invalidateQueries({ queryKey: runsKey(companyId, undefined) });
    },
  });
}

// List borrowing-cost runs (+ lines + approval chain) for a period — drives the
// borrowing tab's "current run status" banner + the run modal's chain view.
export function useCipBorrowingRuns(companyId: string | undefined, period: string | undefined) {
  return useQuery({
    queryKey: runsKey(companyId, period),
    queryFn: () =>
      api<CipBorrowingRunWithChain[]>(
        `/companies/${companyId}/cip-projects/borrowing/runs${period ? `?period=${period}` : ''}`,
      ),
    enabled: !!companyId,
  });
}

// Borrowing-run engine verbs (docType CB). Each invalidates the runs list (chain
// changed), cip-projects + journal-entries (final-step approval posts the
// borrowing JVs and bumps accumulatedCost). Mirrors the AP approve/reject/return
// hooks — engine verbs resolve to the refreshed run via the service's findRun.
function invalidateCipRunEngine(
  qc: ReturnType<typeof useQueryClient>,
  companyId: string | undefined,
) {
  void qc.invalidateQueries({ queryKey: ['cip-projects', companyId, 'borrowing', 'runs'] });
  void qc.invalidateQueries({ queryKey: projectsKey(companyId) });
  void qc.invalidateQueries({ queryKey: ['journal-entries', companyId] });
}

export function useApproveCipBorrowingRun(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      runId,
      step,
      comment,
      sodOverrideReason,
    }: { runId: string } & CipApprovalDecision) =>
      apiJson<CipApprovalDecision, CipBorrowingRunWithChain>(
        'POST',
        `/companies/${companyId}/cip-projects/borrowing/runs/${runId}/approve`,
        withSodOverride({ step, ...(comment !== undefined ? { comment } : {}) }, sodOverrideReason),
      ),
    onSuccess: () => invalidateCipRunEngine(qc, companyId),
  });
}

export function useRejectCipBorrowingRun(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      runId,
      step,
      reason,
      sodOverrideReason,
    }: { runId: string } & CipApprovalReject) =>
      apiJson<CipApprovalReject, CipBorrowingRunWithChain>(
        'POST',
        `/companies/${companyId}/cip-projects/borrowing/runs/${runId}/reject`,
        withSodOverride({ step, reason }, sodOverrideReason),
      ),
    onSuccess: () => invalidateCipRunEngine(qc, companyId),
  });
}

export function useReturnCipBorrowingRun(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      runId,
      step,
      reason,
      sodOverrideReason,
    }: { runId: string } & CipApprovalReject) =>
      apiJson<CipApprovalReject, CipBorrowingRunWithChain>(
        'POST',
        `/companies/${companyId}/cip-projects/borrowing/runs/${runId}/return`,
        withSodOverride({ step, reason }, sodOverrideReason),
      ),
    onSuccess: () => invalidateCipRunEngine(qc, companyId),
  });
}

// List capitalize requests (+ approval chain) for a project — drives the
// capitalize wizard's "already submitted — pending approval" state.
export function useCipCapitalizeRequests(
  companyId: string | undefined,
  cipProjectId: string | undefined,
) {
  return useQuery({
    queryKey: capRequestsKey(companyId, cipProjectId),
    queryFn: () =>
      api<CipCapitalizeRequestWithChain[]>(
        `/companies/${companyId}/cip-projects/${cipProjectId}/capitalize-requests`,
      ),
    enabled: !!companyId && !!cipProjectId,
  });
}

// Capitalize-request engine verbs (docType CI). Final-step approval runs
// capitalize() (posts Dr 1210 / Cr 1240 + registers the FA) and flips to
// CAPITALIZED, so invalidate cip-projects + journal-entries as well.
function invalidateCipRequestEngine(
  qc: ReturnType<typeof useQueryClient>,
  companyId: string | undefined,
) {
  void qc.invalidateQueries({ queryKey: projectsKey(companyId) });
  void qc.invalidateQueries({ queryKey: ['journal-entries', companyId] });
}

export function useApproveCipCapitalizeRequest(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      cipProjectId,
      reqId,
      step,
      comment,
      sodOverrideReason,
    }: { cipProjectId: string; reqId: string } & CipApprovalDecision) =>
      apiJson<CipApprovalDecision, CipCapitalizeRequestWithChain>(
        'POST',
        `/companies/${companyId}/cip-projects/${cipProjectId}/capitalize-requests/${reqId}/approve`,
        withSodOverride({ step, ...(comment !== undefined ? { comment } : {}) }, sodOverrideReason),
      ),
    onSuccess: (_data, vars) => {
      invalidateCipRequestEngine(qc, companyId);
      void qc.invalidateQueries({ queryKey: capRequestsKey(companyId, vars.cipProjectId) });
    },
  });
}

export function useRejectCipCapitalizeRequest(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      cipProjectId,
      reqId,
      step,
      reason,
      sodOverrideReason,
    }: { cipProjectId: string; reqId: string } & CipApprovalReject) =>
      apiJson<CipApprovalReject, CipCapitalizeRequestWithChain>(
        'POST',
        `/companies/${companyId}/cip-projects/${cipProjectId}/capitalize-requests/${reqId}/reject`,
        withSodOverride({ step, reason }, sodOverrideReason),
      ),
    onSuccess: (_data, vars) => {
      invalidateCipRequestEngine(qc, companyId);
      void qc.invalidateQueries({ queryKey: capRequestsKey(companyId, vars.cipProjectId) });
    },
  });
}

export function useReturnCipCapitalizeRequest(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      cipProjectId,
      reqId,
      step,
      reason,
      sodOverrideReason,
    }: { cipProjectId: string; reqId: string } & CipApprovalReject) =>
      apiJson<CipApprovalReject, CipCapitalizeRequestWithChain>(
        'POST',
        `/companies/${companyId}/cip-projects/${cipProjectId}/capitalize-requests/${reqId}/return`,
        withSodOverride({ step, reason }, sodOverrideReason),
      ),
    onSuccess: (_data, vars) => {
      invalidateCipRequestEngine(qc, companyId);
      void qc.invalidateQueries({ queryKey: capRequestsKey(companyId, vars.cipProjectId) });
    },
  });
}

// ============================================================
// Phase 4 — contractor retention (เงินประกันผลงาน)
// ============================================================

// Outstanding retention summary (withheld − released) + release log for a project.
// The detail tab's retention card reads this to show the balance + history.
export function useCipRetention(companyId: string | undefined, cipProjectId: string | undefined) {
  return useQuery({
    queryKey: retentionKey(companyId, cipProjectId),
    queryFn: () =>
      api<CipRetentionResponse>(`/companies/${companyId}/cip-projects/${cipProjectId}/retention`),
    enabled: !!companyId && !!cipProjectId,
  });
}

// Release retention back to the contractor (Dr RETENTION_PAYABLE / Cr cash). The
// release reduces outstanding without touching CIP, so invalidate the project's
// retention key + cip-projects + journal-entries (the JV is visible in /journal).
export function useReleaseCipRetention(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      cipProjectId,
      data,
      idempotencyKey,
    }: {
      cipProjectId: string;
      data: CreateCipRetentionRelease;
      idempotencyKey?: string;
    }) =>
      api<CipRetentionRelease>(
        `/companies/${companyId}/cip-projects/${cipProjectId}/retention/release`,
        {
          method: 'POST',
          headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {},
          body: JSON.stringify(data),
        },
      ),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: retentionKey(companyId, vars.cipProjectId) });
      void qc.invalidateQueries({ queryKey: projectsKey(companyId) });
      void qc.invalidateQueries({ queryKey: ['journal-entries', companyId] });
    },
  });
}

// ============================================================
// Phase 4b — AP-bill → CIP auto-capture (CIP_CLEARING clearing inbox).
// A vendor AP bill tagged to a CIP project routes its work-cost debit to a
// CIP_CLEARING holding account on post. These unconsumed clearing lines surface
// here as a "กล่องรับเข้า (AP)" inbox; receiving one capitalizes it (Dr CIP_ASSET
// / Cr CIP_CLEARING + a CipCostEntry). Mirrors the FA_CLEARING receive flow.
// ============================================================

// One unconsumed Dr CIP_CLEARING journal line awaiting capitalization, as
// returned by GET /cip/clearing-inbox.
export interface CipClearingInboxRow {
  journalLineId: string;
  cipProjectId: string;
  billVoucherNo: string;
  vendorName: string | null;
  amount: string;
  date: string;
}

// List the AP-bill clearing inbox — unconsumed Dr CIP_CLEARING lines, optionally
// scoped to a single project. Drives the CIP page's "กล่องรับเข้า (AP)" tab.
export function useCipClearingInbox(companyId: string | undefined, cipProjectId?: string) {
  return useQuery({
    queryKey: clearingInboxKey(companyId, cipProjectId),
    queryFn: () =>
      api<CipClearingInboxRow[]>(
        `/companies/${companyId}/cip-projects/clearing-inbox${
          cipProjectId ? `?cipProjectId=${cipProjectId}` : ''
        }`,
      ),
    enabled: !!companyId,
  });
}

// Capitalize a clearing line into a project (POST :id/receive-from-clearing).
// Posts Dr CIP_ASSET / Cr CIP_CLEARING + creates a CipCostEntry + bumps
// accumulatedCost, so invalidate the projects list (accumulatedCost changed), the
// project's cost entries (the new entry shows), the clearing inbox (the line is
// consumed), and journal-entries (the JV is visible in /journal).
export function useReceiveFromClearing(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      cipProjectId,
      journalLineId,
      idempotencyKey,
    }: {
      cipProjectId: string;
      journalLineId: string;
      idempotencyKey?: string;
    }) =>
      api<CipCostEntry>(
        `/companies/${companyId}/cip-projects/${cipProjectId}/receive-from-clearing`,
        {
          method: 'POST',
          headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {},
          body: JSON.stringify({ journalLineId }),
        },
      ),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: projectsKey(companyId) });
      void qc.invalidateQueries({ queryKey: entriesKey(companyId, vars.cipProjectId) });
      // Invalidate every clearing-inbox scope (the all-projects view + any
      // project-scoped view) — the consumed line drops from all of them.
      void qc.invalidateQueries({
        queryKey: ['cip-projects', companyId, 'clearing-inbox'],
      });
      void qc.invalidateQueries({ queryKey: ['journal-entries', companyId] });
    },
  });
}
